import { useCallback } from 'react';
import {
	MakerInfo,
	MarketType,
	PositionDirection,
	getUserStatsAccountPublicKey,
	promiseTimeout,
} from '@drift-labs/sdk';
import { ENUM_UTILS } from '@drift/common';
import ExchangeHistoryClient from 'src/utils/exchangeHistoryClient';
import useDriftClient from 'src/hooks/useDriftClient';

export const useGetMakerInfoForMarketOrder = () => {
	const driftClient = useDriftClient();

	const getMakerInfoForMarketOrder = useCallback(
		async ({
			direction,
			marketIndex,
			marketType = MarketType.PERP,
			numMakers,
		}: {
			direction: PositionDirection;
			marketIndex: number;
			marketType?: MarketType;
			numMakers?: number;
		}): Promise<MakerInfo[] | undefined | null> => {
			if (!driftClient) return undefined;

			// Return early if RPC hangs
			const TIMEOUT_SECONDS = 4;

			const resultPromise = (async () => {
				if (ENUM_UTILS.match(marketType, MarketType.SPOT)) return undefined;

				const counterPartySide = ENUM_UTILS.match(
					direction,
					PositionDirection.LONG
				)
					? 'ask'
					: 'bid';

				// spot doesnt support multiple makers so force to 1 even if passed
				const makerLimit = ENUM_UTILS.match(marketType, MarketType.PERP)
					? (numMakers ?? 4)
					: 1;

				// TODO : If we integrate this with the changes in the new market order toasts then there should be a globally available latest slot, instead of needing to fetch it from the RPC here
				const makersResults =
					await ExchangeHistoryClient.getTopMakersKeysAndAccounts({
						marketIndex,
						marketType,
						side: counterPartySide,
						limit: makerLimit,
					});

				if (!makersResults || makersResults.length === 0) {
					return undefined;
				}

				const makerAccountKeys = makersResults.map(
					(maker) => maker.userAccountPubKey
				);
				const makerStatsAccountKeys = makersResults.map((makerAccount) =>
					getUserStatsAccountPublicKey(
						driftClient.program.programId,
						makerAccount.userAccount.authority
					)
				);
				const makerAccounts = makersResults.map((maker) => maker.userAccount);

				return makerAccountKeys.map((makerUserAccountKey, index) => {
					return {
						maker: makerUserAccountKey,
						makerUserAccount: makerAccounts[index],
						makerStats: makerStatsAccountKeys[index],
					};
				});
			})();

			return promiseTimeout(resultPromise, TIMEOUT_SECONDS * 1000);
		},
		[driftClient]
	);

	return { getMakerInfoForMarketOrder };
};
